import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  Checkbox,
  Container,
  CssBaseline,
  FormControlLabel,
  Toolbar,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SitemarkIcon from '../../components/common/SitemarkIcon';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../api';
import { getToken, getUser } from '../../api/auth';
import { useToast } from '../../hooks/useToast';

const SubmitOnboaringScreen = ({ form, setForm, onBack, onNext }) => {
  const navigate = useNavigate();
  const { showToast, ToastComponent } = useToast();
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [errorTerms, setErrorTerms] = useState(false);
  const [loading, setLoading] = useState(false);

  const uploadOnboardingData = async (data) => {
    const token = getToken();
    const user = getUser();

    // El onboarding puede no requerir token si es el primer registro
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['X-Session-Token'] = token;
    }

    if (user?.companyId) {
      headers['X-Company-ID'] = user.companyId;
    }

    const response = await fetch(`${API_BASE_URL}/api/onboarding-assist`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Error al enviar los datos' }));
      throw new Error(error.error || 'Error al enviar los datos');
    }

    return response.json();
  };
      
  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #4a9b94 0%, #3a7a73 100%)',
        fontFamily: 'Inter, Noto Sans, sans-serif',
      }}
    >
      <ToastComponent />
      <CssBaseline />
      <AppBar
        position="static"
        color="transparent"
        elevation={0}
        sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.2)', px: 4, py: 1.5 }}
      >
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <SitemarkIcon />
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
              Bankivo Assist
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 5 }}>
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 600, color: 'white', mb: 1 }}>
            Revisar y Finalizar
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
            Paso 4: Revisa la información antes de continuar
          </Typography>
        </Box>

        <Card sx={{ borderRadius: 2, boxShadow: 4, mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Accordion defaultExpanded sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 2, boxShadow: 'none' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={600}>Información de la Empresa</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Nombre de la empresa
                    </Typography>
                    <Typography variant="body2" color="text.primary">
                      {form.general?.companyName || 'N/A'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Identificación Fiscal
                    </Typography>
                    <Typography variant="body2" color="text.primary">
                      {form.general?.taxId || 'N/A'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Tipo de Financiera
                    </Typography>
                    <Box>
                      {form.general?.companyTypes && form.general.companyTypes.length > 0 ? (
                        form.general.companyTypes.map((type, index) => (
                          <Typography key={index} variant="body2" color="text.primary">
                            {type}
                          </Typography>
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No seleccionado
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Teléfono
                    </Typography>
                    <Typography variant="body2" color="text.primary">
                      {form.general?.phone || 'N/A'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Dirección
                    </Typography>
                    <Typography variant="body2" color="text.primary">
                      {form.general?.address || 'N/A'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      País
                    </Typography>
                    <Typography variant="body2" color="text.primary">
                      {form.general?.country || 'N/A'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Moneda
                    </Typography>
                    <Typography variant="body2" color="text.primary">
                      {form.general?.currency || 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>

            <Accordion defaultExpanded sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 2, boxShadow: 'none' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={600}>Configuración de WhatsApp</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Número de WhatsApp Business
                  </Typography>
                  <Typography variant="body2" color="text.primary">
                    {form.whatsapp?.number || 'N/A'}
                  </Typography>
                </Box>
              </AccordionDetails>
            </Accordion>

            <Accordion defaultExpanded sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 2, boxShadow: 'none' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={600}>Sucursales</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {form.sucursales && form.sucursales.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {form.sucursales.map((sucursal, index) => (
                      <Box key={sucursal.id || index} sx={{ py: 1, borderBottom: index < form.sucursales.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                        <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {sucursal.nombre}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {sucursal.direccion}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {sucursal.contacto}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No hay sucursales agregadas
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>

            <Accordion defaultExpanded sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 2, boxShadow: 'none' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={600}>Usuarios Iniciales</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {form.usuarios ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {Object.entries(form.usuarios).map(([rol, datos]) => (
                      <Box key={rol} sx={{ py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600, mb: 1, textTransform: 'capitalize' }}>
                          {rol}
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pl: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Nombre completo:</strong> {datos.nombre_completo || 'N/A'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Usuario:</strong> {datos.nombre || 'N/A'}
                          </Typography>
                          {rol !== 'administrador' && datos.sucursal && (
                            <Typography variant="body2" color="text.secondary">
                              <strong>Sucursal:</strong> {form.sucursales?.find(s => s.id === datos.sucursal)?.nombre || 'N/A'}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No hay usuarios configurados
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 2, boxShadow: 4 }}>
          <CardContent sx={{ p: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={termsAccepted}
                  onChange={(e) => {
                    setTermsAccepted(e.target.checked);
                    if (e.target.checked) setErrorTerms(false);
                  }}
                />
              }
              label={
                <Typography variant="body2">
                  Estoy de acuerdo con los términos y condiciones
                </Typography>
              }
              sx={{ mb: 2 }}
            />
            {errorTerms && (
              <Typography color="error" variant="caption" sx={{ display: 'block', mb: 2 }}>
                Debes aceptar los términos y condiciones para continuar.
              </Typography>
            )}
          </CardContent>
        </Card>

        <Box display="flex" justifyContent="space-between" mt={4} gap={2}>
          <Button
            onClick={onBack}
            variant="outlined"
            sx={{
              flex: 1,
              fontWeight: 600,
              borderRadius: 1,
              px: 4,
              py: 1.5,
              textTransform: 'none',
              borderColor: 'white',
              color: 'white',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.8)',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            Editar Información
          </Button>

          <Button
            variant="contained"
            disabled={loading}
            onClick={async () => {
              try {
                if (!termsAccepted) {
                  setErrorTerms(true);
                  return;
                }
                setLoading(true);
                const payload = { ...form };
                await uploadOnboardingData(payload);
                showToast('Configuración completada exitosamente', 'success');
                setTimeout(() => {
                  navigate('/inbox', { replace: true });
                }, 1000);
              } catch (error) {
                showToast(error.message || 'No se pudo completar el proceso. Verifica tu conexión o los datos ingresados.', 'error');
                setLoading(false);
              }
            }}
            sx={{
              flex: 1,
              fontWeight: 600,
              borderRadius: 1,
              px: 4,
              py: 1.5,
              textTransform: 'none',
              backgroundColor: 'white',
              color: '#18586b',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
              },
              '&:disabled': {
                backgroundColor: 'rgba(255, 255, 255, 0.5)',
              },
            }}
          >
            {loading ? 'Guardando...' : 'Empezar a Usar Bankivo Assist'}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default SubmitOnboaringScreen;
